import type {
  CustomEventProperties,
  TrackerConfig,
  TrackerInitOptions,
  TrackerLifecycle,
  UplaneApi
} from './contracts';
import { SDK_VERSION } from './contracts';
import { validateConfig } from './config';
import { createLogger, type TrackerLogger } from './logger';
import { IdentityStore } from './storage';
import { sanitizeString } from './sanitize';
import { readCustomEventProperties } from './event-properties';
import { UplaneTracker } from './tracker';
import { BrowserTransport } from './transport';

// The tracking service reads this prefix as the statement that the browser runs anonymously.
const ANONYMOUS_ID_PREFIX = 'anon:';

const doNotTrackEnabled = (): boolean => {
  const legacyNavigator = navigator as Navigator & { msDoNotTrack?: string };
  return navigator.doNotTrack === '1' || legacyNavigator.msDoNotTrack === '1';
};

export const createUplaneApi = (): UplaneApi => {
  let lifecycle: TrackerLifecycle = { status: 'idle' };
  let tracker: UplaneTracker | null = null;
  // The validated config of `tracker`. Assigned together with it and read only while it is set.
  let trackerConfig: TrackerConfig | null = null;
  let lastOptions: TrackerInitOptions | null = null;
  let logger: TrackerLogger = createLogger(false);

  const runningId = (kind: keyof UplaneTracker['identifiers']): string | null => {
    if (lifecycle.status !== 'running' || !tracker || !trackerConfig) return null;
    const id = tracker.identifiers[kind];
    return trackerConfig.privacyMode === 'anonymous' ? `${ANONYMOUS_ID_PREFIX}${id}` : id;
  };

  const initialize = async (
    options: TrackerInitOptions,
    startOptions: { captureInitialPageView?: boolean } = {}
  ): Promise<void> => {
    const config = validateConfig(options);
    lastOptions = options;
    logger = createLogger(config.debugOn, config.workspaceId);

    if (IdentityStore.isOptedOut(logger)) {
      tracker?.stop(false);
      tracker = null;
      lifecycle = { status: 'disabled', reason: 'opt-out' };
      return;
    }

    if (config.respectDoNotTrack && doNotTrackEnabled()) {
      tracker?.stop(false);
      tracker = null;
      lifecycle = { status: 'disabled', reason: 'do-not-track' };
      return;
    }

    if (
      lifecycle.status === 'running'
      && lifecycle.workspaceId === config.workspaceId
      && lifecycle.privacyMode === config.privacyMode
      && lifecycle.policyBasis === config.policyBasis
    ) return;
    tracker?.stop();
    lifecycle = {
      status: 'starting',
      workspaceId: config.workspaceId,
      privacyMode: config.privacyMode,
      policyBasis: config.policyBasis
    };
    const nextTracker = new UplaneTracker(config, new BrowserTransport(config.endpoint, logger), logger);
    tracker = nextTracker;
    trackerConfig = config;

    try {
      await nextTracker.start(startOptions);
      if (tracker !== nextTracker) return;
      const identifiers = nextTracker.identifiers;
      lifecycle = {
        status: 'running',
        workspaceId: config.workspaceId,
        privacyMode: config.privacyMode,
        policyBasis: config.policyBasis,
        visitorId: identifiers.visitorId,
        sessionId: identifiers.sessionId
      };
    } catch (error) {
      nextTracker.stop();
      lifecycle = { status: 'stopped', workspaceId: config.workspaceId };
      throw error;
    }
  };

  const api: UplaneApi = {
    version: SDK_VERSION,

    async init(options) {
      await initialize(options);
    },

    async track(name, properties) {
      const normalizedName = typeof name === 'string' ? sanitizeString(name, 100) : '';
      if (!normalizedName) {
        logger.error('track', {
          outcome: 'dropped',
          error: 'event name is required'
        });
        return;
      }

      let customProperties: CustomEventProperties = {};
      try {
        const result = readCustomEventProperties(properties);
        if (result.status === 'valid') customProperties = result.properties;
        else logger.error('track', { outcome: 'properties_omitted', reason: result.reason });
      } catch {
        // A host-supplied proxy can throw while its properties are inspected.
        logger.error('track', { outcome: 'properties_omitted', reason: 'unreadable_properties' });
      }
      tracker?.track('custom_event', { ...customProperties, name: normalizedName });
      await tracker?.flush();
    },

    identify(userId, traits) {
      tracker?.identify(userId, traits);
    },

    setTraits(traits) {
      tracker?.setTraits(traits);
    },

    async flush() {
      await tracker?.flush();
    },

    async grantConsent() {
      if (!lastOptions) return;
      if (
        lifecycle.status === 'running'
        && lifecycle.privacyMode === 'persistent'
        && lifecycle.policyBasis === 'opt_in'
      ) return;

      const currentTracker = tracker;
      const continuingCurrentPage = currentTracker !== null && lifecycle.status === 'running';
      if (continuingCurrentPage) {
        currentTracker.track('consent_granted');
        await currentTracker.flush();
        currentTracker.stop(false);
        tracker = null;
      }

      await initialize({
        ...lastOptions,
        privacyMode: 'persistent',
        policyBasis: 'opt_in'
      }, { captureInitialPageView: !continuingCurrentPage });
    },

    async revokeConsent() {
      if (!lastOptions) return;
      if (lifecycle.status === 'running' && lifecycle.privacyMode === 'anonymous') return;

      const workspaceId = lastOptions.workspaceId.trim();
      const continuingCurrentPage = tracker !== null && lifecycle.status === 'running';
      tracker?.stop(false);
      tracker = null;
      IdentityStore.clearTrackingIds(workspaceId, logger);
      lifecycle = { status: 'stopped', workspaceId };

      await initialize({
        ...lastOptions,
        privacyMode: 'anonymous',
        policyBasis: 'opt_in'
      }, { captureInitialPageView: !continuingCurrentPage });
    },

    async optIn() {
      IdentityStore.setOptOut(false, logger);
      if (lastOptions) await api.init(lastOptions);
      else lifecycle = { status: 'idle' };
    },

    optOut() {
      IdentityStore.setOptOut(true, logger);
      if (lastOptions) IdentityStore.clearTrackingIds(lastOptions.workspaceId.trim(), logger);
      tracker?.stop(false);
      tracker = null;
      lifecycle = { status: 'disabled', reason: 'opt-out' };
    },

    stop() {
      const workspaceId = lifecycle.status === 'running'
        || lifecycle.status === 'starting'
        ? lifecycle.workspaceId
        : lastOptions?.workspaceId;
      tracker?.stop();
      tracker = null;
      lifecycle = workspaceId ? { status: 'stopped', workspaceId } : { status: 'idle' };
    },

    getState() {
      if (lifecycle.status === 'running' && tracker) {
        const identifiers = tracker.identifiers;
        return {
          ...lifecycle,
          visitorId: identifiers.visitorId,
          sessionId: identifiers.sessionId
        };
      }
      return { ...lifecycle };
    },

    getContext() {
      if (lifecycle.status !== 'running' || !tracker || !trackerConfig) return null;
      return tracker.getContext();
    },

    getDistinctId() {
      return runningId('visitorId');
    },

    getSessionId() {
      return runningId('sessionId');
    },

    decorateUrl(url) {
      if (typeof url !== 'string' || lifecycle.status !== 'running' || !tracker) return url;
      return tracker.decorateUrl(url);
    }
  };

  return api;
};
